/**
 * Basketball Teams Data & Utilities
 * Shared between web, mobile, and server.
 * Single source of truth for team names, abbreviations, and logo URLs.
 */

import { NCAAB_TEAMS, WNCAAB_TEAMS } from './collegeTeams';
import { EUROLEAGUE_TEAM_NAMES, EUROLEAGUE_TEAMS } from './euroleagueTeams';
import { getParentLeague, type LeagueKey } from './league';
import { GENERATED_TEAM_NAMES } from './teamIdentityData.generated';
import { MANUAL_TEAM_NAMES } from './teamIdentityManual';

export interface TeamInfo {
  abbr: string;
  logo: string;
}

export interface TeamWithName extends TeamInfo {
  name: string;
}

export const NBA_TEAMS: Record<string, TeamInfo> = {
  'Atlanta Hawks': {
    abbr: 'ATL',
    logo: 'https://cdn.nba.com/logos/nba/1610612737/global/L/logo.svg',
  },
  'Boston Celtics': {
    abbr: 'BOS',
    logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
  },
  'Brooklyn Nets': {
    abbr: 'BKN',
    logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
  },
  'Charlotte Hornets': {
    abbr: 'CHA',
    logo: 'https://cdn.nba.com/logos/nba/1610612766/global/L/logo.svg',
  },
  'Chicago Bulls': {
    abbr: 'CHI',
    logo: 'https://cdn.nba.com/logos/nba/1610612741/global/L/logo.svg',
  },
  'Cleveland Cavaliers': {
    abbr: 'CLE',
    logo: 'https://cdn.nba.com/logos/nba/1610612739/global/L/logo.svg',
  },
  'Dallas Mavericks': {
    abbr: 'DAL',
    logo: 'https://cdn.nba.com/logos/nba/1610612742/global/L/logo.svg',
  },
  'Denver Nuggets': {
    abbr: 'DEN',
    logo: 'https://cdn.nba.com/logos/nba/1610612743/global/L/logo.svg',
  },
  'Detroit Pistons': {
    abbr: 'DET',
    logo: 'https://cdn.nba.com/logos/nba/1610612765/global/L/logo.svg',
  },
  'Golden State Warriors': {
    abbr: 'GSW',
    logo: 'https://cdn.nba.com/logos/nba/1610612744/global/L/logo.svg',
  },
  'Houston Rockets': {
    abbr: 'HOU',
    logo: 'https://cdn.nba.com/logos/nba/1610612745/global/L/logo.svg',
  },
  'Indiana Pacers': {
    abbr: 'IND',
    logo: 'https://cdn.nba.com/logos/nba/1610612754/global/L/logo.svg',
  },
  'Los Angeles Clippers': {
    abbr: 'LAC',
    logo: 'https://cdn.nba.com/logos/nba/1610612746/global/L/logo.svg',
  },
  'Los Angeles Lakers': {
    abbr: 'LAL',
    logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
  },
  'Memphis Grizzlies': {
    abbr: 'MEM',
    logo: 'https://cdn.nba.com/logos/nba/1610612763/global/L/logo.svg',
  },
  'Miami Heat': {
    abbr: 'MIA',
    logo: 'https://cdn.nba.com/logos/nba/1610612748/global/L/logo.svg',
  },
  'Milwaukee Bucks': {
    abbr: 'MIL',
    logo: 'https://cdn.nba.com/logos/nba/1610612749/global/L/logo.svg',
  },
  'Minnesota Timberwolves': {
    abbr: 'MIN',
    logo: 'https://cdn.nba.com/logos/nba/1610612750/global/L/logo.svg',
  },
  'New Orleans Pelicans': {
    abbr: 'NOP',
    logo: 'https://cdn.nba.com/logos/nba/1610612740/global/L/logo.svg',
  },
  'New York Knicks': {
    abbr: 'NYK',
    logo: 'https://cdn.nba.com/logos/nba/1610612752/global/L/logo.svg',
  },
  'Oklahoma City Thunder': {
    abbr: 'OKC',
    logo: 'https://cdn.nba.com/logos/nba/1610612760/global/L/logo.svg',
  },
  'Orlando Magic': {
    abbr: 'ORL',
    logo: 'https://cdn.nba.com/logos/nba/1610612753/global/L/logo.svg',
  },
  'Philadelphia 76ers': {
    abbr: 'PHI',
    logo: 'https://cdn.nba.com/logos/nba/1610612755/global/L/logo.svg',
  },
  'Phoenix Suns': {
    abbr: 'PHX',
    logo: 'https://cdn.nba.com/logos/nba/1610612756/global/L/logo.svg',
  },
  'Portland Trail Blazers': {
    abbr: 'POR',
    logo: 'https://cdn.nba.com/logos/nba/1610612757/global/L/logo.svg',
  },
  'Sacramento Kings': {
    abbr: 'SAC',
    logo: 'https://cdn.nba.com/logos/nba/1610612758/global/L/logo.svg',
  },
  'San Antonio Spurs': {
    abbr: 'SAS',
    logo: 'https://cdn.nba.com/logos/nba/1610612759/global/L/logo.svg',
  },
  'Toronto Raptors': {
    abbr: 'TOR',
    logo: 'https://cdn.nba.com/logos/nba/1610612761/global/L/logo.svg',
  },
  'Utah Jazz': {
    abbr: 'UTA',
    logo: 'https://cdn.nba.com/logos/nba/1610612762/global/L/logo.svg',
  },
  'Washington Wizards': {
    abbr: 'WAS',
    logo: 'https://cdn.nba.com/logos/nba/1610612764/global/L/logo.svg',
  },
};

export const WNBA_TEAMS: Record<string, TeamInfo> = {
  'Atlanta Dream': {
    abbr: 'ATL',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/atl.png',
  },
  'Chicago Sky': {
    abbr: 'CHI',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/chi.png',
  },
  'Connecticut Sun': {
    abbr: 'CON',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/conn.png',
  },
  'Dallas Wings': {
    abbr: 'DAL',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/dal.png',
  },
  'Golden State Valkyries': {
    abbr: 'GVV',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/gs.png',
  },
  'Indiana Fever': {
    abbr: 'IND',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/ind.png',
  },
  'Las Vegas Aces': {
    abbr: 'LVA',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/lv.png',
  },
  'Los Angeles Sparks': {
    abbr: 'LAS',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/la.png',
  },
  'Minnesota Lynx': {
    abbr: 'MIN',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/min.png',
  },
  'New York Liberty': {
    abbr: 'NYL',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/ny.png',
  },
  'Phoenix Mercury': {
    abbr: 'PHO',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/phx.png',
  },
  'Seattle Storm': {
    abbr: 'SEA',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/sea.png',
  },
  'Washington Mystics': {
    abbr: 'WSH',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/wsh.png',
  },
  'Toronto Tempo': {
    abbr: 'TOR',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/tor.png',
  },
  'Portland Fire': {
    abbr: 'POR',
    logo: 'https://a.espncdn.com/i/teamlogos/wnba/500/por.png',
  },
};

export const EUROLEAGUE_TEAMS_MAP: Record<string, TeamInfo> = Object.fromEntries(
  Object.entries(EUROLEAGUE_TEAMS).map(([name, team]) => [
    name,
    { abbr: team.code, logo: team.crestUrl },
  ]),
);

/**
 * Get the static teams map for a given league display name or league key.
 * Returns null for unknown leagues.
 */
export function getTeamsForLeague(league: string): Record<string, TeamInfo> | null {
  switch (league) {
    case 'NBA':
    case 'NBA Preseason':
    case 'NBA Summer League':
    case 'NBA All-Stars':
    case 'basketball_nba':
      return NBA_TEAMS;
    case 'WNBA':
    case 'basketball_wnba':
      return WNBA_TEAMS;
    case 'EuroLeague':
    case 'basketball_euroleague':
      return EUROLEAGUE_TEAMS_MAP;
    case 'NCAAB':
    case 'basketball_ncaab':
      return NCAAB_TEAMS;
    case 'WNCAAB':
    case 'basketball_wncaab':
      return WNCAAB_TEAMS;
    case 'basketball_nba_preseason':
    case 'basketball_nba_summer_league':
    case 'basketball_nba_all_stars':
      return NBA_TEAMS;
    default:
      return null;
  }
}

/**
 * The key two team names are compared by: lowercase letters and digits only, accents dropped,
 * and "Saint", "State" and "St." all spelled "st", so "Michigan St Spartans" meets
 * "Michigan State Spartans" and "St. Mary's" meets "Saint Mary's". "&" reads as "and", or
 * "Missouri S&T" would meet "Missouri State".
 */
export function teamNameKey(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\b(saint|state)\b/g, 'st')
    .replace(/[^a-z0-9]/g, '');
}

interface TeamIndex {
  byAbbr: Map<string, TeamWithName>;
  byName: Map<string, TeamWithName>;
}

const teamIndexCache = new Map<LeagueKey, TeamIndex>();

/**
 * Every known name of every team in the league's registry. A name that two teams share is left
 * out, so an ambiguous input resolves to nothing rather than to one of them.
 */
function buildTeamIndex(leagueKey: LeagueKey, teams: Record<string, TeamInfo>): TeamIndex {
  const identityKey = getParentLeague(leagueKey);
  // Names a league's own provider table carries, for leagues ESPN's lists do not cover.
  const providerNames: Partial<Record<LeagueKey, Record<string, readonly string[]>>> = {
    basketball_euroleague: EUROLEAGUE_TEAM_NAMES,
  };
  const extraNames = (abbr: string): readonly string[] => [
    ...(GENERATED_TEAM_NAMES[identityKey]?.[abbr] ?? []),
    ...(providerNames[identityKey]?.[abbr] ?? []),
    ...(MANUAL_TEAM_NAMES[identityKey]?.[abbr] ?? []),
  ];
  const byAbbr = new Map<string, TeamWithName>();
  const byName = new Map<string, TeamWithName>();
  const ambiguous = new Set<string>();
  for (const [name, info] of Object.entries(teams)) {
    const team = { name, ...info };
    byAbbr.set(info.abbr.toUpperCase(), team);
    for (const form of [name, ...extraNames(info.abbr)]) {
      const key = teamNameKey(form);
      const existing = byName.get(key);
      if (existing && existing.abbr !== info.abbr) ambiguous.add(key);
      else byName.set(key, team);
    }
  }
  for (const key of ambiguous) byName.delete(key);
  return { byAbbr, byName };
}

/**
 * The registry team an abbreviation or provider name refers to, or null. Exact only: the
 * abbreviation, then any known name by teamNameKey. No partial matching, so a name
 * this does not know returns null instead of the nearest-looking team.
 */
export function resolveTeam(
  input: string | null | undefined,
  leagueKey: LeagueKey | null | undefined,
): TeamWithName | null {
  if (!input || !leagueKey) return null;
  const teams = getTeamsForLeague(leagueKey);
  if (!teams) return null;
  let index = teamIndexCache.get(leagueKey);
  if (!index) {
    index = buildTeamIndex(leagueKey, teams);
    teamIndexCache.set(leagueKey, index);
  }
  return (
    index.byAbbr.get(input.trim().toUpperCase()) ?? index.byName.get(teamNameKey(input)) ?? null
  );
}

/**
 * Find team by name, abbreviation, or partial match within a specific league.
 * Handles various formats: "Lakers", "LAL", "Los Angeles Lakers", "Duke", "UNC", etc.
 *
 * leagueKey should identify which league to search (prevents cross-league
 * collisions like "Houston" NBA vs NCAAB). Returns null when the team name, the
 * leagueKey, or the league's team map is missing — never throws, so callers on a
 * render hot path (e.g. parlay-signature computation) can't crash the view on a
 * leg whose leagueKey was dropped upstream.
 */
/** Which side of a game `team` plays on, or null when it is not a known name for either team. */
export function getTeamSide(
  team: string | null | undefined,
  game: { home_team: string; away_team: string },
  leagueKey: LeagueKey,
): 'home' | 'away' | null {
  const resolved = resolveTeam(team, leagueKey)?.abbr;
  if (!resolved) return null;
  if (resolved === resolveTeam(game.home_team, leagueKey)?.abbr) return 'home';
  if (resolved === resolveTeam(game.away_team, leagueKey)?.abbr) return 'away';
  return null;
}

export const findTeam = (
  teamName: string | null | undefined,
  leagueKey: LeagueKey | null | undefined,
): TeamWithName | null => {
  if (!teamName || !leagueKey) return null;

  const teams = getTeamsForLeague(leagueKey);
  if (!teams) return null;

  const known = resolveTeam(teamName, leagueKey);
  if (known) return known;

  const lower = teamName.trim().toLowerCase();

  // Partial/nickname match, for display only: resolveTeam did not know the name.
  // Scored to prefer better matches (e.g. "North Carolina Tar Heels" -> "North Carolina").
  let bestMatch: TeamWithName | null = null;
  let bestScore = 0;

  for (const [name, data] of Object.entries(teams)) {
    const nameLower = name.toLowerCase();
    const lastWord = name.split(' ').pop()?.toLowerCase() || '';
    let score = 0;

    if (lastWord === lower || lower === nameLower) {
      score = 3;
    } else if (lower.startsWith(`${nameLower} `) || lower.startsWith(nameLower)) {
      // Input starts with team name — prefer longer names
      // "North Carolina Tar Heels" → "North Carolina" (score 17) beats "North" (score 8)
      score = 4 + nameLower.length;
    } else if (nameLower.includes(lower)) {
      score = 2;
    } else if (lower.includes(lastWord)) {
      score = 1;
    }

    if (score > bestScore) {
      bestMatch = { name, ...data };
      bestScore = score;
    }
  }

  return bestMatch;
};

/**
 * Get team info with fallback for unknown teams.
 * Always returns a valid object, never null.
 */
export const getTeamInfo = (
  teamName: string | null | undefined,
  leagueKey: LeagueKey,
): TeamWithName => {
  const team = findTeam(teamName, leagueKey);
  if (team) return team;

  // Fallback for unknown teams
  const name = teamName?.trim() || 'Unknown';
  return {
    name,
    abbr: name.slice(0, 3).toUpperCase(),
    logo: '',
  };
};

/**
 * Get team logo URL by team name.
 */
export const getTeamLogo = (
  teamName: string | null | undefined,
  leagueKey: LeagueKey | null | undefined,
): string => {
  const team = findTeam(teamName, leagueKey);
  return team?.logo || '';
};

/**
 * Get team abbreviation by team name. With no league the name cannot be resolved, so it
 * falls back the same way an unknown team does.
 */
export const getTeamAbbr = (
  teamName: string | null | undefined,
  leagueKey: LeagueKey | null | undefined,
): string => {
  const team = findTeam(teamName, leagueKey);
  return team?.abbr || (teamName?.slice(0, 3).toUpperCase() ?? '???');
};
